<template>
	<Dialog
		v-model:visible="visible"
		modal
		:header="$t('dialogs.device_duplicate_dialog.duplicate_device')"
		:style="{ width: '34rem' }"
		@hide="onHide"
	>
		<div class="flex flex-col gap-6">
			<FormGroup
				:label="$t('dialogs.device_duplicate_dialog.serial_number')"
				:error-text="errors.serial_number"
			>
				<InputText
					v-model="form.serial_number"
					placeholder="e.g. AB23XYZ"
					@input="errors.serial_number = null"
					data-cy="serial-number-input-duplicate-dialog-box"
				/>
			</FormGroup>

			<!-- Asset mode toggle -->
			<FormGroup :label="$t('dialogs.device_duplicate_dialog.asset')">
				<div class="flex gap-4">
					<label class="flex items-center gap-2 cursor-pointer">
						<RadioButton v-model="assetMode" value="existing" />
						<span class="text-sm">{{ $t('dialogs.device_duplicate_dialog.select_existing_asset') }}</span>
					</label>
					<label class="flex items-center gap-2 cursor-pointer">
						<RadioButton v-model="assetMode" value="new" />
						<span class="text-sm">{{ $t('dialogs.device_duplicate_dialog.create_new_asset') }}</span>
					</label>
					<label class="flex items-center gap-2 cursor-pointer">
						<RadioButton v-model="assetMode" value="none" data-cy="select-no-asset" />
						<span class="text-sm">{{ $t('dialogs.device_duplicate_dialog.no_asset') }}</span>
					</label>
				</div>
			</FormGroup>

			<!-- Select existing asset -->
			<FormGroup
				v-if="assetMode === 'existing'"
				:label="$t('dialogs.device_duplicate_dialog.select_asset')"
				:error-text="errors.asset_id"
			>
				<SelectAssetDropdown
					v-model="form.asset_id"
					:asset-filters="assetFilters"
					@update:model-value="errors.asset_id = null"
				/>
			</FormGroup>

			<!-- Create new asset by VIN -->
			<FormGroup
				v-if="assetMode === 'new'"
				:label="$t('dialogs.device_duplicate_dialog.vin_number')"
				:error-text="errors.vin_number"
			>
				<InputText
					v-model="form.vin_number"
					placeholder="e.g. 1HGBH41JXMN109186"
					@input="errors.vin_number = null"
				/>
				<small class="text-surface-500">{{ $t('dialogs.device_duplicate_dialog.vin_hint') }}</small>
			</FormGroup>
		</div>

		<template #footer>
			<Button
				:label="$t('common.cancel')"
				severity="secondary"
				text
				@click="visible = false"
				:disabled="processing"
			/>
			<Button
				:label="$t('dialogs.device_duplicate_dialog.duplicate')"
				@click="submit"
				:disabled="processing || !form.serial_number || (assetMode === 'existing' && !form.asset_id) || (assetMode === 'new' && !form.vin_number)"
				:loading="processing"
				data-cy="duplicate-btn"
			/>
		</template>
	</Dialog>
</template>

<script>
import FormGroup from "@/Components/FormGroup.vue";
import SelectAssetDropdown from "@/Components/SelectAssetDropdown.vue";
import { mapStores } from "pinia";
import { useDevicesStore } from "@/stores/devices.js";

export default {
	name: "DeviceDuplicateDialog",
	components: { FormGroup, SelectAssetDropdown },
	emits: ['on-duplicated'],
	data() {
		return {
			visible: false,
			processing: false,
			deviceId: null,
			companyId: null,
			assetMode: 'none', // 'existing' | 'new' | 'none'
			form: {
				serial_number: '',
				asset_id: null,
				vin_number: '',
			},
			errors: {
				serial_number: null,
				asset_id: null,
				vin_number: null,
			},
		};
	},
	computed: {
		...mapStores(useDevicesStore),
		assetFilters() {
			return this.companyId ? { company_id: this.companyId } : {};
		},
	},
	methods: {
		open(deviceId, companyId) {
			this.deviceId = deviceId;
			this.companyId = companyId ?? null;
			this.assetMode = 'none';
			this.form.serial_number = '';
			this.form.asset_id = null;
			this.form.vin_number = '';
			this.errors.serial_number = null;
			this.errors.asset_id = null;
			this.errors.vin_number = null;
			this.visible = true;
		},
		onHide() {
			this.processing = false;
		},
		async submit() {
			if (this.processing) return;
			this.processing = true;
			try {
				const payload = {
					serial_number: this.form.serial_number,
				};
				if (this.assetMode === 'existing' && this.form.asset_id) {
					payload.asset_id = this.form.asset_id;
				} else if (this.assetMode === 'new' && this.form.vin_number) {
					payload.vin_number = this.form.vin_number;
				}
				const newDevice = await this.devicesStore.duplicateDevice(this.deviceId, payload);
				this.$toast.add({
					severity: 'success',
					summary: this.$t('common.complete'),
					detail: this.$t('dialogs.device_duplicate_dialog.device_duplicated'),
					life: 3000,
				});
				this.visible = false;
				this.$emit('on-duplicated', newDevice);
			} catch (error) {
				if (error?.data?.errors) {
					Object.assign(this.errors, error.data.errors);
				} else {
					this.$toast.add({
						severity: 'error',
						summary: this.$t('common.error'),
						detail: this.$t('dialogs.device_duplicate_dialog.failed_to_duplicate_device'),
						life: 5000,
					});
				}
			} finally {
				this.processing = false;
			}
		},
	},
};
</script>
